// Utility functions

import { startOfWeek } from 'date-fns';

export function formatTime24To12(time24) {
    if (!time24) return '';
    const [hours24, minutes] = time24.split(':');
    const hours12 = ((parseInt(hours24, 10) + 11) % 12 + 1);
    const amPm = parseInt(hours24, 10) >= 12 ? 'PM' : 'AM';
    return `${hours12}:${minutes} ${amPm}`;
}

export const formatTimestamp24To12 = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

export function getMonday(date) {
    return startOfWeek(date, { weekStartsOn: 1 });
}

export function formatTimestamp(formattedDate, time) {
    return time ? `${formattedDate}T${time}:00` : null;
}

export function formatDate(date) {
    if (!date) return "";

    if (!date.toISOString) {
        date = new Date(date);
    }
    
    return date.toISOString().split('T')[0];
};

function isBrowser() {
    return typeof window !== 'undefined';
}

export async function authenticatedFetch(path, options) {
    const url = [process.env.REACT_APP_API_BASE_URL, path].join('');

    if (!options) options = {};
    
    const token = (process.env.UNIVERSAL_PASSWORD && btoa(`:${process.env.UNIVERSAL_PASSWORD}`)) || localStorage.getItem('authToken');

    if (!token) {
        if (isBrowser()) {
            window.dispatchEvent(new CustomEvent('authRequired'));
        }
        else {
            throw new Error('Missing auth token');
        }
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            'Authorization': `Basic ${token}`,
            ...options.headers
        }
    });

    if (response.headers.get('X-App-Version') && isBrowser()) {
        window.X_APP_VERSION = response.headers.get('X-App-Version');
    }

    if (response.status === 401) {
        if (isBrowser()) {
            window.dispatchEvent(new CustomEvent('authRequired'));
        }
        else {
            throw new Error('Unauthorized');
        }
    }

    return response;
}

//convert a Time string (HH:MM:SS) to a Date object
export function timeToDate(time) {
    const [hours, minutes, seconds] = time.split(':');
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(seconds);
    return date;
}

export function roundToNearestTenth(num) {
    return Math.round(num * 10) / 10;
}

export function isWeekend(date) {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
}

// Local calendar date as "YYYY-MM-DD", ignoring time-of-day and timezone.
// (Unlike formatDate, which uses toISOString/UTC and rolls to the next day in the evening for negative-offset zones.)
function localDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// Determines whether selectedDate is a school day. Compares on calendar date
// only — first_day/last_day/holiday.date are "YYYY-MM-DD" strings, and
// selectedDate carries a local time-of-day, so string comparison of the local
// date key avoids the UTC off-by-one that treated the last day as Summer Break.
export function getSchoolStatus(selectedDate, calendarInfo) {
    if (isWeekend(selectedDate)) {
        return { isSchoolDay: false, message: "No School: Weekend" };
    }

    if (!calendarInfo) {
        return { isSchoolDay: false, message: "No School: Summer Break" };
    }

    const dateKey = localDateKey(selectedDate);

    const holiday = calendarInfo.holidays.find(h => h.date === dateKey);
    if (holiday) {
        return { isSchoolDay: false, message: `No School: ${holiday.name}` };
    }

    if (dateKey < calendarInfo.first_day || dateKey > calendarInfo.last_day) {
        return { isSchoolDay: false, message: "No School: Summer Break" };
    }

    return { isSchoolDay: true, message: "" };
}

export async function isSchoolDay(date) {
    // Fetch the calendar data to check for holidays and get school year dates
    const calendarResponse = await authenticatedFetch(`/calendar/for-day/${formatDate(date)}`);
    const calendarData = await calendarResponse.json();

    if (!calendarData) {
        return false;
    }

    const firstDayOfSchool = new Date(calendarData.first_day + 'T00:00:00-04:00'); // Ensure Eastern Time
    const lastDayOfSchool = new Date(calendarData.last_day + 'T00:00:00-04:00'); // Ensure Eastern Time
    const holidays = calendarData.holidays.map(holiday => holiday.date);

    if (date < firstDayOfSchool || date > lastDayOfSchool) {
        return false;
    }

    return !isWeekend(date) && !holidays.includes(formatDate(date));
}

/**
 * Get display text for absence/attendance status
 *
 * Logic:
 * - Returns "—" for incomplete/missing records
 * - Returns "Holiday" for holidays
 * - Returns granted_reason if granted_hours > 0
 * - Returns "Personal Day" if no granted hours OR day not earned
 * - Returns "Earned Day" (report mode) or "—" (UI mode) if day earned
 *
 * @param {Object} attendance - Attendance record with NEW schema fields
 * @param {string|null} attendance.signed_in_at - Sign in time
 * @param {string|null} attendance.signed_out_at - Sign out time
 * @param {number|null} attendance.granted_hours - Hours granted
 * @param {string|null} attendance.granted_reason - Reason for granted hours
 * @param {boolean|null} attendance.day_earned - Whether minimum hours met
 * @param {number|null} attendance.id - Record ID (null if no record)
 * @param {Object} options - Display options
 * @param {boolean} options.isHoliday - Whether day is a holiday
 * @param {boolean} options.showEarnedDay - Show "Earned Day" instead of "—" when day_earned=true
 * @returns {string|JSX} Display text or error indicator
 */
export function getAbsenceDisplay(attendance, options = {}) {
    // 1. Handle no record or incomplete record
    if (!attendance || !attendance.id) {
        return "—";  // No record exists
    }

    const hasSignIn = !!attendance.signed_in_at;
    const hasSignOut = !!attendance.signed_out_at;

    // Incomplete record (partial data)
    if ((hasSignIn && !hasSignOut) || (!hasSignIn && hasSignOut)) {
        return "—";  // Missing either sign in or sign out
    }

    // 2. Handle holidays
    if (options.isHoliday) {
        return "Holiday";
    }

    // 3. Check if absent (no sign in/out times)
    const isAbsent = !hasSignIn && !hasSignOut;

    if (isAbsent) {
        // Granted absence with hours
        if (attendance.granted_hours > 0) {
            if (!attendance.granted_reason) {
                return "!";  // Error indicator - caller should style as red
            }
            return attendance.granted_reason;
        }
        // Personal day (unexcused absence)
        return "Personal Day";
    }

    // 4. Student is present (has BOTH sign in AND sign out times)

    // Check if day was earned
    if (attendance.day_earned === true) {
        // Granted hours while present (partial day, appointment, etc)
        if (attendance.granted_hours > 0) {
            if (!attendance.granted_reason) {
                return "!";  // Error indicator - caller should style as red
            }
            return attendance.granted_reason;
        }

        // Normal attendance - earned the day
        if (options.showEarnedDay) {
            return "Earned Day"; // For attendance-report.js
        }
        return "—"; // For Attendance.js and StudentAttendance.js
    }

    // Day not earned (day_earned === false or null)
    // Personal day despite being present (didn't meet minimum hours)
    return "Personal Day";
}